import { useToaster } from '@/components/shared/ToasterProvider'
import { RouteDefinition, Method } from '@/routes/runtime'

export function getCSRFToken(): string {
  return document
    .querySelector('meta[name="csrf-token"]')!
    .getAttribute('content')!
}

export function getRequestHeaders(): Record<string, string> {
  return {
    'X-CSRF-Token': getCSRFToken(),
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
}


function parseResponseBody(rawBody: string): any {
  if (!rawBody) return null

  return JSON.parse(rawBody)
}

export function formatErrorMessage(data: unknown, response: Response): string {
  if (typeof data === 'string' && data.trim()) return data
  if (data && typeof data === 'object') {
    if (Array.isArray((data as any).errors) && (data as any).errors.length > 0) return (data as any).errors.join(', ')
    if (typeof (data as any).errors === 'string') return (data as any).errors
    if (typeof (data as any).error === 'string') return (data as any).error
  }

  return `${response.status} ${response.statusText}`.trim()
}

class ApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'CustomError'
    this.status = status
  }
}

interface FetchDataProps {
  headers?: Record<string, string>
  body?: BodyInit
}

export async function fetchData<T>({ url, method }: RouteDefinition<Method>, {
  headers = getRequestHeaders(),
  body,
}: FetchDataProps = {}): Promise<T> {
  let response: Response
  let data: T

  try {
    // Don't set Content-Type for FormData - browser sets it automatically with the correct boundary
    if (body instanceof FormData) {
      delete headers['Content-Type']
    }
    response = await fetch(url, { method: method.toUpperCase(), headers, body })
    const rawBody = await response.text()
    data = parseResponseBody(rawBody)
    if (response.ok) return data

    const apiErrorMessage = formatErrorMessage(data, response)
    console.error(`API Error: ${apiErrorMessage}`)
    return Promise.reject(new ApiError(apiErrorMessage, response.status))
  } catch (error) {
    const wrappedError = error instanceof Error ? error : new Error(String(error))
    console.error(wrappedError.message)
    throw wrappedError
  }
}

// useActionFetch is intended for user-initiated requests (e.g. button clicks, form submits).
// It wraps fetchData and automatically shows a generic error toast on failure,
// so callers don't need to handle errors themselves.
export function useActionFetch() {
  const showToaster = useToaster()

  async function actionFetch<T>(routeDefinition: RouteDefinition<Method>, {
    headers = getRequestHeaders(),
    body,
  }: FetchDataProps = {}): Promise<T> {
    try {
      return await fetchData<T>(routeDefinition, { headers, body })
    } catch (err) {
      showToaster({ message: 'Something went wrong. Please try again!' })
      // throw is required here since otherwise `then` on the parent promise will always be called
      // even when nothing of data is returned leading to calling `then` handler with undefined -
      // hiding the error in that case and potentially causing unexpected behavior in the app
      // since the error is already handled by showing toaster but the flow continues as if the request was
      // successful since return data is unknown, sometimes some record or array, we need to avoid calling
      // then on actionFetch when error happens since there is no certain default value that can be returned
      // here in the catch - so throw error and avoid then
      throw err
    }
  }

  return { actionFetch }
}
